import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import {
  CheckCircle2, XCircle, Clock, Eye, MessageSquare,
  Shield, Brain, ChevronRight, Filter, RotateCcw,
  FileText, Image as ImageIcon, Share2, Star, AlertTriangle,
  ThumbsUp, ThumbsDown, Send, Layers
} from 'lucide-react';

interface QueueItem {
  id: string;
  title: string;
  type: 'blog' | 'social' | 'infographic' | 'carousel' | 'thread';
  platform: string;
  domain: string;
  submittedAt: string;
  wordCount?: number;
  brandScore: number;
  aiConfidence: number;
  humanScore: number;
  trustScore: number;
  conversionScore: number;
  preview: string;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected';
  reviewer?: string;
  flags: string[];
}

const QUEUE: QueueItem[] = [
  {
    id: 'q1', title: 'The Rise of Decentralised Clinical Trials: What 2026 Means for Sponsors',
    type: 'blog', platform: 'WordPress', domain: 'Clinical Research', submittedAt: '2 min ago',
    wordCount: 1420, brandScore: 91, aiConfidence: 88, humanScore: 84, trustScore: 79, conversionScore: 72,
    status: 'reviewing',
    preview: 'Decentralised clinical trials (DCTs) have moved from experimental fringe to regulatory mainstream...',
    flags: [], reviewer: 'AI Writer v2'
  },
  {
    id: 'q2', title: '5 Ways Hospitality Brands Are Winning With AI Guest Personalisation',
    type: 'blog', platform: 'LinkedIn Newsletter', domain: 'Hospitality', submittedAt: '8 min ago',
    wordCount: 980, brandScore: 78, aiConfidence: 92, humanScore: 70, trustScore: 65, conversionScore: 81,
    status: 'pending',
    preview: 'Hyatt, Marriott and a wave of boutique operators are deploying ML-powered concierge layers...',
    flags: ['Low humanisation score'], reviewer: undefined
  },
  {
    id: 'q3', title: 'Real-World Evidence: 3 Regulatory Shifts Reshaping Drug Development',
    type: 'infographic', platform: 'Instagram', domain: 'Clinical Research', submittedAt: '14 min ago',
    brandScore: 95, aiConfidence: 96, humanScore: 88, trustScore: 91, conversionScore: 68,
    status: 'pending',
    preview: 'Hero stat: 68% of FDA submissions now include RWE data. Sources: FDA, NEJM, EMA 2025.',
    flags: [], reviewer: undefined
  },
  {
    id: 'q4', title: 'Hotel Tech Stack 2026: From PMS to AI Concierge — Full Thread',
    type: 'thread', platform: 'X / Twitter', domain: 'Hospitality', submittedAt: '22 min ago',
    brandScore: 72, aiConfidence: 85, humanScore: 61, trustScore: 55, conversionScore: 77,
    status: 'pending',
    preview: '1/ The average hotel now runs 14 disconnected software tools. Here\'s how the stack is collapsing into 3 platforms...',
    flags: ['Low trust score', 'Needs citations'], reviewer: undefined
  },
  {
    id: 'q5', title: 'Compound GLP-1 Controversy: What Compounding Pharmacies Need to Know',
    type: 'carousel', platform: 'LinkedIn', domain: 'Pharma', submittedAt: '31 min ago',
    wordCount: 620, brandScore: 88, aiConfidence: 90, humanScore: 82, trustScore: 86, conversionScore: 75,
    status: 'approved',
    preview: 'Slide 1: FDA\'s Q1 2026 guidance on compounded semaglutide has split the industry...',
    flags: [], reviewer: 'Sarah M.'
  },
];

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  blog: FileText, infographic: ImageIcon, carousel: Layers,
  social: Share2, thread: MessageSquare,
};

const TYPE_COLORS: Record<string, string> = {
  blog: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  infographic: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
  carousel: 'text-fuchsia-400 bg-fuchsia-500/10 border-fuchsia-500/20',
  social: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  thread: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   color: 'text-amber-400  bg-amber-400/10  border-amber-400/20'  },
  reviewing: { label: 'Reviewing', color: 'text-blue-400   bg-blue-400/10   border-blue-400/20'   },
  approved:  { label: 'Approved',  color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
  rejected:  { label: 'Rejected',  color: 'text-red-400    bg-red-400/10    border-red-400/20'    },
};

function ScoreMeter({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-500">{label}</span>
        <span className={`text-[11px] font-bold ${value >= 80 ? 'text-emerald-400' : value >= 60 ? 'text-amber-400' : 'text-red-400'}`}>{value}</span>
      </div>
      <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
    </div>
  );
}

const Approval: React.FC = () => {
  const [items, setItems] = useState<QueueItem[]>(QUEUE);
  const [selected, setSelected] = useState<QueueItem | null>(QUEUE[0]);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'reviewing' | 'approved' | 'rejected'>('all');
  const [note, setNote] = useState('');

  const filtered = filterStatus === 'all' ? items : items.filter(i => i.status === filterStatus);

  const counts = {
    pending:   items.filter(i => i.status === 'pending').length,
    reviewing: items.filter(i => i.status === 'reviewing').length,
    approved:  items.filter(i => i.status === 'approved').length,
    rejected:  items.filter(i => i.status === 'rejected').length,
  };

  const avgBrand  = Math.round(items.reduce((s, i) => s + i.brandScore, 0) / items.length);
  const avgAI     = Math.round(items.reduce((s, i) => s + i.aiConfidence, 0) / items.length);

  const handleApprove = (id: string) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, status: 'approved' } : i));
    if (selected?.id === id) setSelected(prev => prev ? { ...prev, status: 'approved' } : null);
  };

  const handleReject = (id: string) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, status: 'rejected' } : i));
    if (selected?.id === id) setSelected(prev => prev ? { ...prev, status: 'rejected' } : null);
  };

  const TypeIcon = selected ? (TYPE_ICONS[selected.type] || FileText) : FileText;

  return (
    <Layout>
      <div className="flex flex-col h-full bg-slate-950">

        {/* ── Header ──────────────────────────────────────────── */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-800/60 flex-shrink-0">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-100">Approval Queue</h1>
              <p className="text-sm text-slate-500 mt-0.5">Brand compliance & quality gate before distribution</p>
            </div>
            <button className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Refresh Queue
            </button>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Pending Review',   value: counts.pending,   icon: Clock,       color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20' },
              { label: 'In Review',        value: counts.reviewing, icon: Eye,         color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20' },
              { label: 'Avg Brand Score',  value: `${avgBrand}%`,   icon: Shield,      color: 'text-violet-400',  bg: 'bg-violet-500/10 border-violet-500/20' },
              { label: 'AI Confidence',    value: `${avgAI}%`,      icon: Brain,       color: 'text-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/20' },
            ].map(k => (
              <div key={k.label} className={`rounded-xl border p-3 ${k.bg}`}>
                <div className="flex items-center gap-2 mb-1">
                  <k.icon className={`w-4 h-4 ${k.color}`} />
                  <span className="text-[11px] text-slate-500">{k.label}</span>
                </div>
                <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Body: Queue list + Detail panel ─────────────────── */}
        <div className="flex flex-1 overflow-hidden">

          {/* Queue list */}
          <div className="w-full md:w-[42%] border-r border-slate-800/60 flex flex-col overflow-hidden flex-shrink-0">
            {/* Filter tabs */}
            <div className="flex items-center gap-1 px-4 py-3 border-b border-slate-800/60 flex-shrink-0">
              <Filter className="w-3.5 h-3.5 text-slate-600 mr-1" />
              {(['all', 'pending', 'reviewing', 'approved', 'rejected'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors capitalize ${
                    filterStatus === s
                      ? 'bg-violet-500/15 text-violet-400 border border-violet-500/20'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {s}
                  {s !== 'all' && counts[s] > 0 && (
                    <span className="ml-1 text-[10px] opacity-70">({counts[s as keyof typeof counts]})</span>
                  )}
                </button>
              ))}
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto">
              <AnimatePresence>
                {filtered.map((item, idx) => {
                  const Icon = TYPE_ICONS[item.type] || FileText;
                  const sc = STATUS_CONFIG[item.status];
                  const isSelected = selected?.id === item.id;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      onClick={() => setSelected(item)}
                      className={`px-4 py-3.5 border-b border-slate-800/40 cursor-pointer transition-all ${
                        isSelected ? 'bg-violet-500/8 border-l-2 border-l-violet-500' : 'hover:bg-slate-900/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 rounded-lg border flex-shrink-0 mt-0.5 ${TYPE_COLORS[item.type]}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-1.5 py-0.5 rounded-md border text-[9px] font-semibold uppercase ${sc.color}`}>
                              {sc.label}
                            </span>
                            {item.flags.length > 0 && (
                              <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-slate-200 font-medium leading-snug line-clamp-2">{item.title}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-[10px] text-slate-600">{item.domain}</span>
                            <span className="text-[10px] text-slate-700">·</span>
                            <span className="text-[10px] text-slate-600">{item.platform}</span>
                            <span className="text-[10px] text-slate-700">·</span>
                            <span className="text-[10px] text-slate-600">{item.submittedAt}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex items-center gap-1">
                              <Shield className="w-3 h-3 text-violet-500" />
                              <span className={`text-[10px] font-semibold ${item.brandScore >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{item.brandScore}%</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Brain className="w-3 h-3 text-cyan-500" />
                              <span className={`text-[10px] font-semibold ${item.aiConfidence >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{item.aiConfidence}%</span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 transition-colors ${isSelected ? 'text-violet-400' : 'text-slate-700'}`} />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>

          {/* Detail panel */}
          <div className="hidden md:flex flex-1 flex-col overflow-hidden">
            {selected ? (
              <div className="flex flex-col h-full">
                {/* Detail header */}
                <div className="px-6 py-4 border-b border-slate-800/60 flex-shrink-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`p-2 rounded-xl border flex-shrink-0 ${TYPE_COLORS[selected.type]}`}>
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-base font-semibold text-slate-100 leading-snug">{selected.title}</h2>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase ${STATUS_CONFIG[selected.status].color}`}>
                            {STATUS_CONFIG[selected.status].label}
                          </span>
                          <span className="text-[11px] text-slate-500">{selected.platform}</span>
                          <span className="text-[11px] text-slate-600">·</span>
                          <span className="text-[11px] text-slate-500">{selected.domain}</span>
                          {selected.wordCount && (
                            <>
                              <span className="text-[11px] text-slate-600">·</span>
                              <span className="text-[11px] text-slate-500">{selected.wordCount.toLocaleString()} words</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    {(selected.status === 'pending' || selected.status === 'reviewing') && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleReject(selected.id)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl text-red-400 text-xs font-semibold transition-colors"
                        >
                          <ThumbsDown className="w-3.5 h-3.5" /> Reject
                        </button>
                        <button
                          onClick={() => handleApprove(selected.id)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-xl text-emerald-400 text-xs font-semibold transition-colors"
                        >
                          <ThumbsUp className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => handleApprove(selected.id)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/25 rounded-xl text-violet-300 text-xs font-semibold transition-colors"
                        >
                          <Send className="w-3.5 h-3.5" /> Approve & Publish
                        </button>
                      </div>
                    )}
                    {selected.status === 'approved' && (
                      <span className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-xs font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approved
                      </span>
                    )}
                    {selected.status === 'rejected' && (
                      <span className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-semibold">
                        <XCircle className="w-3.5 h-3.5" /> Rejected
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {/* Flags */}
                  {selected.flags.length > 0 && (
                    <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-semibold text-amber-400">Quality Flags</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selected.flags.map(f => (
                          <span key={f} className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-xs">{f}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quality Scoring Matrix */}
                  <div className="bg-slate-900/60 rounded-xl border border-slate-800/60 p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="w-4 h-4 text-violet-400" />
                      <span className="text-sm font-semibold text-slate-200">Quality Scoring Matrix</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      <ScoreMeter label="Brand Alignment"  value={selected.brandScore}    color="bg-violet-500" />
                      <ScoreMeter label="AI Confidence"    value={selected.aiConfidence}  color="bg-cyan-500" />
                      <ScoreMeter label="Humanisation"     value={selected.humanScore}    color="bg-fuchsia-500" />
                      <ScoreMeter label="Trust & Citations" value={selected.trustScore}   color="bg-emerald-500" />
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-800/60">
                      <ScoreMeter label="Conversion Potential" value={selected.conversionScore} color="bg-amber-500" />
                    </div>
                  </div>

                  {/* Content Preview */}
                  <div className="bg-slate-900/60 rounded-xl border border-slate-800/60 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-semibold text-slate-200">Content Preview</span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed italic">"{selected.preview}"</p>
                    <p className="text-[11px] text-slate-600 mt-2">Generated by: {selected.reviewer || 'AI Writer v2'}</p>
                  </div>

                  {/* Reviewer note */}
                  {(selected.status === 'pending' || selected.status === 'reviewing') && (
                    <div className="bg-slate-900/60 rounded-xl border border-slate-800/60 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-semibold text-slate-200">Review Note</span>
                      </div>
                      <textarea
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Add a review note or rejection reason..."
                        className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-3 py-2.5 text-sm text-slate-300 placeholder:text-slate-600 resize-none focus:outline-none focus:border-violet-500/50 transition-colors"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Eye className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Select an item to review</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Approval;
